import React, { useState, useEffect } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { getBlobRequestWithAuth } from '~/lib/api'
import { LuImageOff } from 'react-icons/lu'

interface AssetImageComponentProps {
  node: {
    attrs: {
      sessionId: string
      filename: string
      alt?: string
    }
  }
  deleteNode: () => void
}

const AssetImageComponent: React.FC<AssetImageComponentProps> = ({ node, deleteNode }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { sessionId, filename, alt } = node.attrs

  useEffect(() => {
    let objectUrl: string | null = null

    const loadImage = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        const response = await getBlobRequestWithAuth(
          `/api/v1/editor/getAssetImage/${sessionId}/${filename}`
        )
        
        const blob = response.data
        objectUrl = URL.createObjectURL(blob)
        setImageSrc(objectUrl)
      } catch (err) {
        console.error('Error loading asset image:', err)
        setError('Failed to load chart')
      } finally {
        setIsLoading(false)
      }
    }

    if (sessionId && filename) {
      loadImage()
    }

    // Cleanup blob URL on unmount
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [sessionId, filename])

  if (isLoading) {
    return (
      <NodeViewWrapper className="my-4 inline-block">
        <div className="flex flex-col items-center justify-center gap-2 p-12 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 min-h-[200px]">
          <div className="loading loading-spinner loading-md"></div>
          <span>Loading chart...</span>
        </div>
      </NodeViewWrapper>
    )
  }

  if (error || !imageSrc) {
    return (
      <NodeViewWrapper className="my-4 inline-block">
        <div className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-error/30 rounded-lg bg-error/5 min-h-[150px]">
          <LuImageOff className="w-8 h-8 text-error" />
          <span className="text-error">Chart failed to load</span>
        </div>
      </NodeViewWrapper>
    )
  }

  return (
    <NodeViewWrapper className="my-4 inline-block">
      <div className="inline-block rounded-lg overflow-hidden max-w-full">
        <img 
          src={imageSrc} 
          alt={alt || `Chart: ${filename}`} 
          className="block max-w-full h-auto"
        />
      </div>
    </NodeViewWrapper>
  )
}

export default AssetImageComponent